import { useState, forwardRef, useEffect } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";

import { Thumbnails } from "./Components/Thumbnails";
import EmbeddablePlayer from "../../../EmbeddablePlayer";
import { NoClips } from "../NoClips";
import {
  ActivePlaylistClipState,
  BlockedClipState,
  BlockedPlayerNameState,
  DisplayEmptyState,
  NextUpPlaylistState,
  PlaylistIdState,
  ShowWatchLimitBlockerState,
} from "MVVM/State/embeddablePlaylist";
import { TRNEmbeddablePlaylistViewModel } from "./ViewModel";
import { WatchLimitBlocker } from "../WatchLimitBlocker";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import { NextUpPlayer } from "../NextUpPlayer";

export const TRNPlaylist = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    handleMouseEnter: () => void;
  }
>(({ platform, playlist, handleMouseEnter }, ref) => {
  const [selectedTag] = useState<string>("all");
  const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
  const playlistId = useRecoilValue(PlaylistIdState);
  const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
  const blockedClip = useRecoilValue(BlockedClipState);
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const displayEmptyState = useRecoilValue(DisplayEmptyState);
  const setIsNextUpPlaylist = useSetRecoilState(NextUpPlaylistState);
  const renderNextUpPlayer = false;

  const { getClipsForTag } = TRNEmbeddablePlaylistViewModel({
    playlist,
    platform,
  });
  const { handleWatchLimitReached, handleCloseBlocker } = useWatchLimit();
  const clipsToDisplay = getClipsForTag(selectedTag);

  useEffect(() => {
    setIsNextUpPlaylist(true);
  }, [setIsNextUpPlaylist, playlist]);

  if (renderNextUpPlayer) {
    return (
      <NextUpPlayer
        playlist={playlist}
        platform={platform}
        primaryColor="#f1ed62"
        secondaryColor="#1b181f"
      />
    );
  }

  return (
    <>
      <Flex flexDirection={"column"} style={{ height: "100%", width: "100%" }}>
        <StyledContainer onMouseEnter={handleMouseEnter}>
          <StyledGradientLayer className="gradient-layer">
            <StyledHeaderContainer>
              <Flex style={{ gap: "5px", alignItems: "center" }}>
                <TRNLogo
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/trn.png`}
                />
                <Text variant="text.labelXSmall" fontWeight={700}>
                  {activePlaylistClip?.title}
                </Text>
              </Flex>
              <Flex alignItems="center" style={{ gap: "4px" }}>
                <StyledPoweredByText variant="text.labelXXSmall">
                  Powered by
                </StyledPoweredByText>
                <Image
                  width={48}
                  height={10}
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/allstargg.png`}
                  onClick={() => {
                    const ajs_aid = window.rudderanalytics.getAnonymousId();
                    return window.open(
                      `https://allstar.gg?utm_source=${platform}&ajs_aid=${ajs_aid}`,
                      "_blank",
                    );
                  }}
                />
              </Flex>
            </StyledHeaderContainer>
            <StyledWrapper>
              <FixedPlayerWrapper ref={ref} className="fixed-width-container">
                {activePlaylistClip && (
                  <EmbeddablePlayer
                    className="next-up-player"
                    onPlayEndVariant="nextUp"
                  />
                )}
                {displayEmptyState && <NoClips />}
                {showWatchLimitBlocker && (
                  <WatchLimitBlocker
                    onClose={handleCloseBlocker}
                    playerName={blockedPlayerName}
                    clickedClip={blockedClip}
                    primaryColor="#f1ed62"
                    secondaryColor="#1b181f"
                    playlistId={playlistId}
                  />
                )}
              </FixedPlayerWrapper>
              <StyledPlayerImage
                src={
                  process.env.REACT_APP_S3_CLIP_BUCKET +
                  "/static/web/iframe/Lockup2.png"
                }
                onClick={() => {
                  const ajs_aid = window.rudderanalytics.getAnonymousId();
                  window.open(
                    `https://allstar.gg?utm_source=${platform}&ajs_aid=${ajs_aid}`,
                    "_blank",
                  );
                }}
              />
            </StyledWrapper>

            <Thumbnails
              clipsToDisplay={clipsToDisplay}
              onWatchLimitReached={handleWatchLimitReached}
            />
          </StyledGradientLayer>
        </StyledContainer>
      </Flex>
    </>
  );
});

const StyledPoweredByText = styled(Text)`
  color: #cddcfe;
`;
const TRNLogo = styled(Image)`
  height: 11px;
  width: 32px;
`;

const StyledHeaderContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;
const StyledPlayerImage = styled(Image)`
  position: absolute;
  right: 8px;
  top: 30%;
  width: 55px;
  z-index: 100;
`;

const StyledWrapper = styled(Box)`
  min-height: 271px;
  position: relative;

  @media (max-width: 502px) {
    min-height: 210px;
  }

  @media (max-width: 425px) {
    min-height: 185px;
  }

  @media (max-width: 380px) {
    min-height: 153px;
  }
`;

const FixedPlayerWrapper = styled(Box)`
  bottom: 0;
  height: 271px;
  left: 0;
  position: absolute;
  top: 0;
  width: 512px;
  z-index: 1;

  &:not(:has(:fullscreen)) {
    .vjs-tech {
      border-radius: 8px;
      height: auto !important;
      width: 100% !important;
    }
    .video-js {
      background: transparent;
      width: 100% !important;
      height: 100% !important;
      min-height: 271px;
    }
  }

  @media (max-width: 502px) {
    .embeddable-player {
      .video-js {
        min-height: 207px;
      }
    }
  }

  @media (max-width: 425px) {
    .embeddable-player {
      .video-js {
        min-height: 185px;
      }
    }
  }

  @media (max-width: 380px) {
    .embeddable-player {
      .video-js {
        min-height: 153px;
      }
    }
  }
`;

const StyledGradientLayer = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  height: 100%;
  padding: 10px;
  width: 100%;

  @media (max-width: 502px) {
    gap: 8px;
    padding: 8px;
  }

  @media (max-width: 425px) {
    padding: 6px;
  }
`;

const StyledContainer = styled(Flex)`
  background-image: url(${process.env
    .REACT_APP_S3_CLIP_BUCKET}/static/web/trnPlaylistBackground.png);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100%;

  @media (max-width: 502px) {
    .fixed-width-container {
      height: 207px;
      width: 368px;
    }
  }

  @media (max-width: 425px) {
    .fixed-width-container {
      height: 185px;
      width: 330px;
    }
  }

  @media (max-width: 380px) {
    .fixed-width-container {
      height: 153px;
      width: 271px;
    }
  }
`;
